import { useEffect, useMemo, useRef, useState } from "react";
import { Upload, Plus, Trash2, FileDown, X, FileText, CheckCircle2, Clock, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  submissionApi,
  readFileAsBase64,
  triggerDownload,
  MAX_FILE_BYTES,
  MAX_FILES_PER_SUBMISSION,
} from "@/lib/submissionApi";

const MONTHS = [
  { v: 1,  en: "Jan" }, { v: 2,  en: "Feb" }, { v: 3,  en: "Mar" },
  { v: 4,  en: "Apr" }, { v: 5,  en: "May" }, { v: 6,  en: "Jun" },
  { v: 7,  en: "Jul" }, { v: 8,  en: "Aug" }, { v: 9,  en: "Sep" },
  { v: 10, en: "Oct" }, { v: 11, en: "Nov" }, { v: 12, en: "Dec" },
];

function humanSize(n) {
  if (!n) return "0 KB";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

// Submission records section for the school user.
// - Year + Month selectors
// - "+ Add files" (image/* + application/pdf) — multiple selection allowed
// - Optional note
// - Upload button creates the submission
// - List of past submissions for this school with per-file download and an
//   "Add more files" inline button (+ icon) per submission.
export default function SubmissionRecords() {
  const yearChoices = useMemo(() => {
    const cy = new Date().getFullYear();
    return [cy - 1, cy, cy + 1];
  }, []);
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [note, setNote] = useState("");
  const [pending, setPending] = useState([]); // {name,mime,size,dataB64}
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const pickerRef = useRef(null);
  const appendPickers = useRef({});

  const refresh = async () => {
    setLoading(true);
    try { setItems(await submissionApi.list()); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const handlePick = async (evt, target = "new") => {
    const files = Array.from(evt.target.files || []);
    evt.target.value = ""; // allow picking same file twice
    if (files.length === 0) return;
    const limitFor = target === "new"
      ? MAX_FILES_PER_SUBMISSION - pending.length
      : MAX_FILES_PER_SUBMISSION - (items.find((s) => s.id === target)?.files?.length || 0);
    if (limitFor <= 0) {
      toast.error(`Max ${MAX_FILES_PER_SUBMISSION} files per submission`);
      return;
    }
    const accepted = files.slice(0, limitFor);
    const tooBig = accepted.filter((f) => f.size > MAX_FILE_BYTES);
    if (tooBig.length) {
      toast.error(`${tooBig.length} file(s) > 8 MB — skipped`);
    }
    const ok = accepted.filter((f) => f.size <= MAX_FILE_BYTES);
    if (ok.length === 0) return;
    const encoded = await Promise.all(ok.map(readFileAsBase64));
    if (target === "new") {
      setPending((p) => [...p, ...encoded]);
    } else {
      try {
        await submissionApi.appendFiles(target, encoded);
        toast.success(`Added ${encoded.length} file(s)`);
        await refresh();
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Append failed");
      }
    }
  };

  const removePending = (idx) => setPending((p) => p.filter((_, i) => i !== idx));

  const submit = async () => {
    if (pending.length === 0) { toast.error("Attach at least one file first"); return; }
    setSubmitting(true);
    try {
      await submissionApi.create({ year, month, note, files: pending });
      toast.success("Submission sent — our team will get back to you soon");
      setPending([]);
      setNote("");
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setSubmitting(false); }
  };

  const download = async (subId, file) => {
    try {
      const blob = await submissionApi.downloadFile(subId, file.id);
      triggerDownload(blob);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  return (
    <section
      className="rounded-2xl border border-border bg-white p-5 shadow-[0_1px_0_rgba(44,53,40,0.04)] sm:p-6"
      data-testid="submission-records"
    >
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-clay/15 text-clay">
          <Upload className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-heading text-xl font-semibold text-ink-900">Submit Daily Enrolment Photos</h2>
          <p className="mt-1 text-sm text-ink-700">
            Send a photo of your school's Daily Enrolment register at the end of each month — our team will prepare
            your <b>Bill, Voucher &amp; UC</b> and return them on WhatsApp.
          </p>
        </div>
      </div>

      {/* Year / Month / Note + file pile */}
      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <label className="block">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Year</span>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger data-testid="submission-year" className="mt-1.5 h-10 border-border bg-white text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{yearChoices.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </label>
        <label className="block">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Month</span>
          <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
            <SelectTrigger data-testid="submission-month" className="mt-1.5 h-10 border-border bg-white text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{MONTHS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.en}</SelectItem>)}</SelectContent>
          </Select>
        </label>
        <label className="block">
          <span className="block text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ink-700">Optional note</span>
          <input
            data-testid="submission-note"
            type="text"
            value={note}
            placeholder="e.g. Page 12-15 of register"
            onChange={(e) => setNote(e.target.value)}
            className="mt-1.5 block h-10 w-full rounded-lg border border-border bg-white px-3 text-sm text-ink-900 outline-none focus:border-clay focus:ring-1 focus:ring-clay/30"
          />
        </label>
      </div>

      {/* File picker */}
      <input
        ref={pickerRef}
        type="file"
        multiple
        accept="image/*,application/pdf"
        className="hidden"
        data-testid="submission-file-input"
        onChange={(e) => handlePick(e, "new")}
      />
      <div className="mt-4 rounded-2xl border-2 border-dashed border-clay/30 bg-clay/[0.03] p-5">
        {pending.length === 0 ? (
          <div className="text-center">
            <Upload className="mx-auto h-7 w-7 text-clay" />
            <p className="mt-2 text-sm font-semibold text-ink-900">Add photos / PDF</p>
            <p className="text-xs text-ink-700">Up to {MAX_FILES_PER_SUBMISSION} files · max 8 MB each</p>
            <button
              type="button"
              onClick={() => pickerRef.current?.click()}
              data-testid="submission-add-files-btn"
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-clay/40 bg-white px-4 py-2 text-xs font-bold text-clay active:scale-[0.99]"
            >
              <Plus className="h-3.5 w-3.5" /> Add files
            </button>
          </div>
        ) : (
          <>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-clay">{pending.length} file(s) ready</p>
            <ul className="mt-3 space-y-2">
              {pending.map((f, i) => (
                <li key={i} className="flex items-center gap-3 rounded-xl bg-white px-3 py-2 text-sm shadow-sm" data-testid={`submission-pending-${i}`}>
                  <FileText className="h-4 w-4 shrink-0 text-clay" />
                  <span className="min-w-0 flex-1 truncate text-ink-900">{f.name}</span>
                  <span className="shrink-0 text-[0.7rem] text-ink-700">{humanSize(f.size)}</span>
                  <button type="button" onClick={() => removePending(i)} className="grid h-7 w-7 place-items-center rounded-full text-destructive active:bg-red-50">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => pickerRef.current?.click()}
                data-testid="submission-add-more-files-btn"
                className="inline-flex items-center gap-2 rounded-full border border-clay/40 bg-white px-4 py-2 text-xs font-bold text-clay"
              >
                <Plus className="h-3.5 w-3.5" /> Add more files
              </button>
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="submission-submit-btn"
                className="gap-2 bg-clay text-white hover:bg-clay-dark disabled:opacity-50"
              >
                <Upload className="h-4 w-4" /> {submitting ? "Sending…" : "Send to admin"}
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Past submissions */}
      <div className="mt-6">
        <div className="mb-3 flex items-baseline justify-between">
          <h3 className="font-heading text-base font-semibold text-ink-900">Your submissions</h3>
          <span className="text-xs text-ink-700">{items.length} total</span>
        </div>
        {loading ? (
          <p className="rounded-xl bg-sand-50 px-3 py-2 text-xs text-ink-700">Loading…</p>
        ) : items.length === 0 ? (
          <p className="rounded-xl bg-sand-50 px-3 py-3 text-center text-xs text-ink-700">No submissions yet.</p>
        ) : (
          <ul className="space-y-3">
            {items.map((s) => (
              <li key={s.id} className="rounded-2xl border border-border bg-white p-4" data-testid={`submission-item-${s.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-heading text-sm font-bold text-ink-900">
                      {MONTHS.find((m) => m.v === s.month)?.en} {s.year}
                    </p>
                    <p className="text-[0.7rem] text-ink-700">{new Date(s.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}</p>
                    {s.note && <p className="mt-1 text-xs text-ink-700">"{s.note}"</p>}
                  </div>
                  {s.status === "replied" ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-moss/15 px-2.5 py-0.5 text-[0.65rem] font-semibold text-moss-dark">
                      <CheckCircle2 className="h-3 w-3" /> Replied
                    </span>
                  ) : (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-ochre/15 px-2.5 py-0.5 text-[0.65rem] font-semibold text-ochre">
                      <Clock className="h-3 w-3" /> Pending
                    </span>
                  )}
                </div>
                <ul className="mt-3 space-y-1.5">
                  {(s.files || []).map((f) => (
                    <li key={f.id} className="flex items-center gap-2 rounded-lg border border-border bg-sand-50 px-3 py-1.5">
                      <FileText className="h-3.5 w-3.5 shrink-0 text-clay" />
                      <span className="min-w-0 flex-1 truncate text-xs text-ink-900">{f.name}</span>
                      <span className="shrink-0 text-[0.65rem] text-ink-700">{humanSize(f.size)}</span>
                      <button
                        type="button"
                        onClick={() => download(s.id, f)}
                        data-testid={`submission-${s.id}-download-${f.id}`}
                        className="grid h-7 w-7 place-items-center rounded-full text-clay hover:bg-clay/10"
                        aria-label="Download"
                      >
                        <FileDown className="h-3.5 w-3.5" />
                      </button>
                    </li>
                  ))}
                </ul>
                {(s.files?.length || 0) < MAX_FILES_PER_SUBMISSION && (
                  <>
                    <input
                      type="file"
                      multiple
                      accept="image/*,application/pdf"
                      className="hidden"
                      ref={(el) => { if (el) appendPickers.current[s.id] = el; }}
                      onChange={(e) => handlePick(e, s.id)}
                    />
                    <button
                      type="button"
                      onClick={() => appendPickers.current[s.id]?.click()}
                      data-testid={`submission-${s.id}-append-btn`}
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-clay/40 bg-white px-3 py-1.5 text-[0.7rem] font-bold text-clay"
                    >
                      <Plus className="h-3 w-3" /> Add more files
                    </button>
                  </>
                )}
                {s.adminReply && (
                  <div className="mt-3 rounded-xl border border-moss/30 bg-moss/5 p-3" data-testid={`submission-${s.id}-reply`}>
                    <p className="flex items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.16em] text-moss-dark">
                      <MessageSquare className="h-3 w-3" /> Admin reply
                    </p>
                    <p className="mt-1 whitespace-pre-line text-xs text-ink-900">{s.adminReply}</p>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
